import { BaseController } from "../core/BaseController.js";

// The About dialog, which is also the credits.
//
// It was two dialogs — a short "About" with a link into a separate "Sources &
// licences" — and the link was a seam rather than a feature: what the map is and
// what it is made of are the same question asked at two depths, and a reader who
// wanted the licence on a boundary source had to find a second modal to reach
// it. One dialog, with the depth handled by disclosure instead.
//
// The obligations are what decide which sections start open. Two of the four
// boundary sources are CC BY-NC-SA 4.0 and one is GPL-3.0, and those licences
// want a creator, a title, the licence itself and an indication that the
// material was modified — so the boundary section, the indicator citations and
// the map's own licence are expanded when the dialog opens. Only the software
// list, which is a courtesy (those notices travel in the files themselves),
// starts closed.
//
// Everything below `#aboutCredits` is rendered from `/api/config/attribution`,
// generated by scripts/build_attribution.sh from the geometry actually in
// `demscore.countries`. Nothing here knows the name of a single source, which is
// the point: a boundary source added to the database reaches this dialog by the
// build, and the build fails if nobody has written its licence down.
export class AboutController extends BaseController {
  constructor({ meta, attributionMeta }) {
    super();
    this.meta = meta;
    this.attributionMeta = attributionMeta || null;

    this.dialog = this.getEl("aboutDialog");
    this.bodyEl = this.getEl("aboutBody");
    this.creditsEl = this.getEl("aboutCredits");
    this.closeBtn = this.getEl("aboutCloseBtn");
    this.openBtn = this.getEl("aboutBtn");

    // Where each named section landed, so an opener that asked for one can
    // scroll to it. Filled by render().
    this.sections = new Map();
    // Whatever had focus when the dialog opened. The usual opener is the button
    // inside the OpenLayers attribution control, which that control rebuilds
    // whenever the layers change — so remembering the element is not enough on
    // its own, and restoreFocus checks it is still in the document.
    this.opener = null;
    this.rendered = false;
  }

  initialize() {
    this.openBtn?.addEventListener("click", () => this.open(this.openBtn));
    this.closeBtn.addEventListener("click", () => this.dialog.close());
    // Native `close` covers the close button, Escape and a form submission
    // alike, so the opener's state is restored from one place whatever shut it.
    this.dialog.addEventListener("close", () => {
      this.openBtn?.setAttribute("aria-expanded", "false");
      this.restoreFocus();
    });
  }

  // `section` is the key of a credits section to reveal — "boundary" when the
  // reader arrived from the attribution line or the data table's footnote,
  // since that is the question they were asking.
  open(opener = null, section = null) {
    this.opener = opener || (document.activeElement instanceof HTMLElement ? document.activeElement : null);

    // Rendered once. The credits are a build artefact fetched at startup and
    // nothing in the map's state changes them, so redrawing per opening would
    // only throw away whatever the reader had expanded.
    if (!this.rendered) {
      this.render();
      this.rendered = true;
    }

    if (!this.dialog.open) {
      this.openBtn?.setAttribute("aria-expanded", "true");
      if (typeof this.dialog.showModal === "function") this.dialog.showModal();
      else this.dialog.setAttribute("open", "");
    }

    this.revealSection(section);
  }

  // Open the asked-for section and bring it to the top of the scroll box. The
  // dialog has just been shown, so this waits a frame: a scroll set on an
  // element the browser has not laid out yet is a scroll to nowhere.
  revealSection(key) {
    const target = key ? this.sections.get(key) : null;
    if (!target) {
      this.bodyEl.scrollTop = 0;
      return;
    }
    target.open = true;
    requestAnimationFrame(() => {
      this.bodyEl.scrollTop = target.offsetTop - this.bodyEl.offsetTop;
    });
  }

  restoreFocus() {
    if (this.opener && this.opener.isConnected) this.opener.focus();
    this.opener = null;
  }

  // ---- rendering ----------------------------------------------------------

  render() {
    this.creditsEl.innerHTML = "";
    this.sections.clear();
    const meta = this.attributionMeta;

    if (!meta || !Array.isArray(meta.boundary) || meta.boundary.length === 0) {
      // The credits could not be loaded, which is a failure worth naming rather
      // than an empty panel: the reader is owed the sources, and the page cannot
      // supply them.
      const fallback = this.paragraph(
        "The full source and licence register could not be loaded. The boundary data on this " +
          "map comes from Natural Earth (public domain), CShapes 2.0 and CShapes-Europe " +
          "(CC BY-NC-SA 4.0) and Historical Basemaps (GPL-3.0), modified by this deployment.",
      );
      fallback.className = "about-note";
      this.creditsEl.appendChild(fallback);
      return;
    }

    this.creditsEl.appendChild(this.renderIndicatorSection(meta));
    this.creditsEl.appendChild(this.renderBoundarySection(meta));
    this.creditsEl.appendChild(this.renderMapOutputSection(meta));
    this.creditsEl.appendChild(this.renderSoftwareSection(meta));

    if (meta.generatedAt) {
      const stamp = this.paragraph(
        `Sources rebuilt ${meta.generatedAt.slice(0, 10)} from the geometry on this map.`,
      );
      stamp.className = "about-stamp";
      this.creditsEl.appendChild(stamp);
    }
  }

  // The numbers the hexagons are coloured by. First, because it is what a
  // reader opening "About" most likely wants to know.
  renderIndicatorSection(meta) {
    const datasets = Object.entries(meta.indicatorDatasets || {});
    const section = this.section("indicators", "Indicator data", {
      count: `${datasets.length} ${datasets.length === 1 ? "module" : "modules"}`,
      open: true,
      lead: "What the colours measure. Each module asks to be cited as a condition of use.",
    });

    for (const [key, entry] of datasets) {
      const article = document.createElement("article");
      article.className = "about-entry";

      const heading = document.createElement("h4");
      heading.className = "about-entry-title";
      heading.appendChild(this.maybeLink(entry.title, entry.url));
      article.appendChild(heading);

      // The codebook and the download this deployment actually serves, taken
      // from the same dataset config the variable cards link to, so there is one
      // answer to "where did this come from" rather than two.
      const dataset = this.meta?.datasets?.[key];
      const links = [
        { url: dataset?.codebook, text: "Codebook (PDF)" },
        { url: dataset?.download, text: "Download from Demscore" },
      ].filter(({ url }) => typeof url === "string" && url.length > 0);
      if (links.length > 0) {
        const row = document.createElement("p");
        row.className = "about-links";
        for (const { url, text } of links) row.appendChild(this.maybeLink(text, url));
        article.appendChild(row);
      }

      // Behind a disclosure: a V-Dem citation is thirty-six names, and printing
      // three of those in full is a wall in front of everything after it. It is
      // one press away and it is complete, which is what the condition asks for.
      const citations = [entry.citation, entry.alsoCite].filter(Boolean);
      if (citations.length > 0) {
        article.appendChild(
          this.disclosure("How to cite", citations.map((text) => {
            const p = this.paragraph(text);
            p.className = "about-citation";
            return p;
          })),
        );
      }

      section.appendChild(article);
    }

    if (meta.indicatorNote) {
      const note = this.paragraph(meta.indicatorNote);
      note.className = "about-note";
      section.appendChild(note);
    }
    return section;
  }

  // The boundary credits, and the section this dialog exists for. Open by
  // default and never behind a second click: this is where the CC BY-NC-SA and
  // GPL-3.0 attribution is discharged.
  renderBoundarySection(meta) {
    const hexSize = Math.round((meta.weightHexSizeM || 100000) / 1000);
    const section = this.section("boundary", "Map boundaries", {
      count: `${meta.boundary.length} sources`,
      open: true,
      lead:
        "Every hexagon is cut from one of these. What each contributes is measured against " +
        `the database rather than written by hand; counts are at the ${hexSize} km hexagon size.`,
    });

    for (const block of meta.boundary) {
      const entry = document.createElement("article");
      entry.className = "about-entry";

      const heading = document.createElement("h4");
      heading.className = "about-entry-title";
      heading.appendChild(this.maybeLink(block.title, block.url));
      heading.appendChild(this.licenseBadge(block.license));
      entry.appendChild(heading);

      if (block.role) entry.appendChild(this.paragraph(block.role));

      const weight = this.paragraph(this.describeWeight(block));
      weight.className = "about-weight";
      entry.appendChild(weight);

      const detail = [];
      if (block.modifications) {
        const modified = this.paragraph(block.modifications);
        modified.className = "about-modified";
        // The one thing a CC BY-style licence asks for that is easiest to leave
        // out, so it is labelled rather than buried in a sentence.
        modified.prepend(this.strong("Modified: "));
        detail.push(modified);
      }
      if (block.citation) {
        const citation = this.paragraph(block.citation);
        citation.className = "about-citation";
        detail.push(citation);
      }
      if (detail.length > 0) entry.appendChild(this.disclosure("Citation and what we changed", detail));

      section.appendChild(entry);
    }
    return section;
  }

  // How much of the map a source is responsible for. Present-day and dated
  // countries are counted apart because a source can be both: Natural Earth
  // draws 242 current countries and also lends its outline to one historical
  // coding unit.
  describeWeight(block) {
    const parts = [];
    if (block.presentDay > 0) {
      parts.push(`${block.presentDay} present-day ${block.presentDay === 1 ? "country" : "countries"}`);
    }
    const dated = (block.countries || 0) - (block.presentDay || 0);
    if (dated > 0) {
      const span = block.yearFrom && block.yearTo ? `, ${block.yearFrom}–${block.yearTo}` : "";
      parts.push(`${dated} historical ${dated === 1 ? "period" : "periods"}${span}`);
    }
    const hexes = block.hexes || 0;
    return (
      `${parts.join(" and ") || "No countries currently drawn"} — ` +
      `${hexes.toLocaleString()} ${hexes === 1 ? "hexagon" : "hexagons"}`
    );
  }

  renderMapOutputSection(meta) {
    const output = meta.mapOutput || {};
    const section = this.section("using", "Using this map", {
      count: output.license?.label || "",
      open: true,
    });

    if (output.statement) section.appendChild(this.paragraph(output.statement));
    if (output.note) {
      const note = this.paragraph(output.note);
      note.className = "about-note";
      section.appendChild(note);
    }
    return section;
  }

  // Closed by default, because none of it is owed: these are tools whose
  // notices travel in the files they are served from. It is here as a courtesy
  // and because a reader asking what the page is built from should not have to
  // read the source.
  renderSoftwareSection(meta) {
    const section = this.section("software", "Software and images", { open: false });

    const list = document.createElement("ul");
    list.className = "about-list";
    for (const entry of meta.software || []) {
      const item = document.createElement("li");
      item.appendChild(this.maybeLink(entry.version ? `${entry.name} ${entry.version}` : entry.name, entry.url));
      item.appendChild(this.muted(entry.license));
      list.appendChild(item);
    }
    for (const entry of meta.media || []) {
      const item = document.createElement("li");
      item.appendChild(document.createTextNode(`${entry.title}: `));
      item.appendChild(this.maybeLink(entry.credit, entry.url));
      item.appendChild(this.muted(entry.license, entry.licenseUrl));
      list.appendChild(item);
    }
    section.appendChild(list);
    return section;
  }

  // ---- small builders -----------------------------------------------------

  // A top-level section. A <details> even when it starts open, so a reader can
  // fold away what they have read and see the shape of the whole thing; the
  // marker is drawn in CSS, and `open` is what says which ones carry weight.
  section(key, title, { count = "", open = false, lead = "" } = {}) {
    const section = document.createElement("details");
    section.className = "about-section";
    section.open = open;

    const summary = document.createElement("summary");
    summary.className = "about-summary";
    const name = document.createElement("span");
    name.className = "about-summary-title";
    name.textContent = title;
    summary.appendChild(name);
    if (count) {
      const meta = document.createElement("span");
      meta.className = "about-summary-meta";
      meta.textContent = count;
      summary.appendChild(meta);
    }
    section.appendChild(summary);

    if (lead) {
      const intro = this.paragraph(lead);
      intro.className = "about-lead";
      section.appendChild(intro);
    }

    this.sections.set(key, section);
    return section;
  }

  // A disclosure inside an entry, for the text that has to be present and
  // complete but would otherwise bury what is around it.
  disclosure(label, children) {
    const details = document.createElement("details");
    details.className = "about-disclosure";
    const summary = document.createElement("summary");
    summary.textContent = label;
    details.appendChild(summary);
    for (const child of children) details.appendChild(child);
    return details;
  }

  paragraph(text) {
    const element = document.createElement("p");
    element.textContent = text;
    return element;
  }

  strong(text) {
    const element = document.createElement("strong");
    element.textContent = text;
    return element;
  }

  // The licence at the end of a software row: quieter than the name it follows,
  // and a link when the licence has a deed to point at.
  muted(text, url = null) {
    const span = document.createElement("span");
    span.className = "about-list-license";
    span.appendChild(this.maybeLink(text, url));
    return span;
  }

  // A link when there is somewhere to go, plain text when there is not. A
  // project-local polygon has no URL, and an anchor with no href is a tab stop
  // that does nothing.
  maybeLink(text, url) {
    if (typeof url !== "string" || url.length === 0) return document.createTextNode(String(text ?? ""));
    const link = document.createElement("a");
    link.href = url;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    link.textContent = String(text ?? url);
    return link;
  }

  licenseBadge(license) {
    const span = document.createElement("span");
    span.className = "about-license";
    // The terracotta the handbook reserves for exactly this: a condition that
    // limits what the reader may do with what they are looking at.
    if (license?.nonCommercial) span.classList.add("is-restrictive");
    const label = license?.label || "Licence not recorded";
    span.appendChild(this.maybeLink(label, license?.url));
    return span;
  }
}
